// hooks
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type ChangeEvent } from 'react';

// styles
import {
  CompactGridContainer,
  RecurrenceIntervalInput,
  RecurrenceRepeatBox
} from '../../RecurrencePatternBuilder.styles';
import { SpacedBox } from 'components/mrp/components/AppointmentForm/AppointmentForm.styles';

// components
import {
  GridCompact12,
  GridCompact6
} from 'components/shared/Grid/Grid.components';
import Select from 'components/shared/Select';
import WeekDayPicker from '../WeekDayPicker/WeekDayPicker';
import { DatePicker } from 'components/shared/index';

// types
import type {
  DayIndex,
  RecurrencePattern,
  RecurrenceRule
} from '../../RecurrencePatternBuilder.types';
import type { RecurrenceRangeTypeRequest } from 'generated/index';
import type { RecurrenceRulesSectionProp } from './RecurrenceRulesSection.types';

// libs
import dayjs from 'dayjs';

const RecurrenceRulesSection = ({
  minPatternEnd,
  hideWeekDayPicker,
  disableRecurrenceRule
}: RecurrenceRulesSectionProp) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'mrp.appointmentForm' });
  const { setValue, control } = useFormContext<RecurrencePattern>();

  const recurrenceRuleOptions: RecurrenceRule[] = ['none', 'weekly'];
  const recurrenceRangeOptions: RecurrenceRangeTypeRequest[] = [
    'NO_END',
    'END_ON_DATE'
  ];

  const [
    recurrenceRule,
    recurrenceRangeType,
    recurrenceInterval,
    recurrenceEnd,
    daysSelected
  ] = useWatch({
    name: [
      'recurrenceRule',
      'recurrenceRangeType',
      'recurrenceInterval',
      'recurrenceEnd',
      'daysSelected'
    ],
    control
  });

  const endDate = new Date(recurrenceEnd!);

  const updateOptions = {
    shouldValidate: true,
    shouldDirty: true
  };

  const handleRecurrenceRuleChange = (rule: RecurrenceRule) => {
    setValue('recurrenceRule', rule, updateOptions);
  };

  const handleRecurrenceIntervalInputChange = (
    ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = ev.currentTarget.value;
    if (newValue.length > 2) {
      ev.preventDefault();
      return;
    }

    const parsedValue = newValue.length > 0 ? parseInt(newValue) : 1;

    if (isNaN(parsedValue) || parsedValue === 0) {
      ev.preventDefault();
      return;
    }

    setValue('recurrenceInterval', parsedValue, updateOptions);
  };

  const handleDateChange = (newDate: Date | null) => {
    setValue('recurrenceEnd', newDate?.toISOString(), updateOptions);
  };

  const handleWeekDaySelectionChange = (daysSelected: DayIndex[]) => {
    setValue('daysSelected', daysSelected, updateOptions);
  };

  const handleRecurrenceRangeChange = (
    rangeType: RecurrenceRangeTypeRequest
  ) => {
    setValue('recurrenceRangeType', rangeType, updateOptions);
  };

  return (
    <SpacedBox>
      <CompactGridContainer>
        <GridCompact6>
          <Select
            label={t('recurrence')}
            variant="outlined"
            valueProp={recurrenceRule}
            disabled={disableRecurrenceRule}
            options={recurrenceRuleOptions.map((opt) => ({
              label: t(opt),
              value: opt
            }))}
            onSelected={handleRecurrenceRuleChange}
            openOnEnter={true}
          />
        </GridCompact6>
        {recurrenceRule != 'none' && (
          <>
            <GridCompact6>
              <RecurrenceRepeatBox>
                {t('repeatEvery')}
                <RecurrenceIntervalInput
                  value={recurrenceInterval}
                  onChange={handleRecurrenceIntervalInputChange}
                  slotProps={{
                    htmlInput: {
                      sx: { textAlign: 'center' }
                    }
                  }}
                />
                {t('plural_' + recurrenceRule)}
              </RecurrenceRepeatBox>
            </GridCompact6>
            <GridCompact6>
              <Select
                label={t('recurrenceRange')}
                variant="outlined"
                valueProp={recurrenceRangeType}
                options={recurrenceRangeOptions.map((opt) => ({
                  label: t(opt),
                  value: opt
                }))}
                onSelected={handleRecurrenceRangeChange}
              />
            </GridCompact6>
            {recurrenceRangeType == 'NO_END' && <GridCompact6></GridCompact6>}
            {recurrenceRangeType == 'END_ON_DATE' && (
              <GridCompact6>
                <DatePicker
                  clearable={false}
                  date={endDate}
                  label={t('date')}
                  minDate={dayjs(minPatternEnd).toDate()}
                  onChange={handleDateChange}
                  size="small"
                />
              </GridCompact6>
            )}
            {recurrenceRule == 'weekly' && !hideWeekDayPicker && (
              <GridCompact12>
                <WeekDayPicker
                  daysSelected={daysSelected || []}
                  onChange={handleWeekDaySelectionChange}
                />
              </GridCompact12>
            )}
          </>
        )}
      </CompactGridContainer>
    </SpacedBox>
  );
};

export default RecurrenceRulesSection;
